import { HttpClient, HttpContext } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, fromEvent, interval, merge } from 'rxjs';
import { catchError, exhaustMap, filter } from 'rxjs/operators';
import { BASE_PATH, PaymentStatusDto } from '@api-client';
import { SILENT_HTTP_ERRORS } from '../../shared/interceptors/server-http.interceptor';

/** How often the shell re-reads its payment status (the API serves it from a Redis cache). */
export const POLL_INTERVAL_MS = 60_000;

/**
 * Keeps the shell's payment status current without a page reload (ZEY-3061).
 *
 * The settings response carries the status only once, at startup. ZeymoAdmin changes (forced
 * signal, custom days, defaults) clear the tenant's cache, so re-reading
 * `GET /Settings/PaymentStatus` every minute and whenever the tab comes back into view brings
 * them to open tabs. A null answer or a failed call keeps the last known status; failures stay
 * silent (no "server not reachable" toast every minute).
 */
@Injectable({ providedIn: 'root' })
export class PaymentStatusPollService implements OnDestroy {
    private readonly http = inject(HttpClient);
    private readonly basePath = inject(BASE_PATH, { optional: true }) ?? '';
    private readonly document = inject(DOCUMENT);

    private readonly subject = new BehaviorSubject<PaymentStatusDto | undefined>(undefined);
    readonly latest$: Observable<PaymentStatusDto | undefined> = this.subject.asObservable();

    private sub?: Subscription;

    start(): void {
        if (this.sub) return;
        const visible = () => this.document.visibilityState !== 'hidden';
        this.sub = merge(interval(POLL_INTERVAL_MS), fromEvent(this.document, 'visibilitychange'))
            .pipe(
                filter(visible),
                exhaustMap(() =>
                    this.http
                        .get<PaymentStatusDto | null>(`${this.basePath}/Settings/PaymentStatus`, {
                            context: new HttpContext().set(SILENT_HTTP_ERRORS, true),
                        })
                        .pipe(catchError(() => EMPTY)),
                ),
            )
            .subscribe((status) => {
                if (status) this.subject.next(status);
            });
    }

    stop(): void {
        this.sub?.unsubscribe();
        this.sub = undefined;
    }

    ngOnDestroy(): void {
        this.stop();
    }
}

/** The Banner Test override wins, then the polled status, then the one from the startup settings. */
export function pickPaymentStatus(
    fromSettings: PaymentStatusDto | null | undefined,
    polled: PaymentStatusDto | undefined,
    override: PaymentStatusDto | null | undefined,
): PaymentStatusDto | null | undefined {
    return override ?? polled ?? fromSettings;
}
